/**
 * Industrial Tax (Income Tax) Section
 * Extracted from FinancePage.jsx
 */

import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Receipt, Percent, CreditCard, AlertTriangle, ArrowLeft, Target, CheckCircle, ArrowUpRight, ArrowDownRight, Info } from 'lucide-react';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle
} from '../../utils/timeLabels';

const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

// Compact KPI Tooltip - Standard Format (Balanço Patrimonial style)
const CompactKPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 bottom-full mb-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -bottom-1 border-b border-r" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, value, unit, vsBudgetPct, yoyChange, subtitle, isPP = false, tooltipData }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const displayValue = (unit && (unit.includes('M') || unit === 'M')) 
    ? formatM(value) 
    : value;
  
  // For costs/expenses (negative values), favorable = actual less negative than budget
  const isBudgetFavorable = vsBudgetPct !== undefined ? (value < 0 ? vsBudgetPct < 0 : vsBudgetPct >= 0) : true;
  const isYoYFavorable = yoyChange !== undefined ? (value < 0 ? yoyChange < 0 : yoyChange >= 0) : true;
  
  return (
    <div 
      className={`bg-white rounded-xl border border-slate-200 p-5 hover:shadow-lg transition-all duration-300 relative overflow-visible ${tooltipData ? 'cursor-help' : ''}`}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Compact Tooltip */}
      {tooltipData && (
        <CompactKPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
      
      {/* Trend Arrow Indicator - Corner Icon */}
      <div 
        className={`absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center ${
          isYoYFavorable ? 'bg-emerald-100' : 'bg-rose-100'
        }`}
      >
        {isYoYFavorable ? (
          <ArrowDownRight className="w-4 h-4 text-emerald-600" />
        ) : (
          <ArrowUpRight className="w-4 h-4 text-rose-600" />
        )}
      </div>
      
      <p className="text-xs text-slate-500 tracking-wider mb-1 pr-8">{title}</p>
      <p className="font-serif text-3xl font-semibold text-primary">
        {displayValue}<span className="text-base text-slate-400 ml-1 font-sans">{unit}</span>
      </p>
      {/* Budget and YoY badges */}
      {(vsBudgetPct !== undefined || yoyChange !== undefined) && (
        <div className="flex flex-col gap-1 mt-2">
          {vsBudgetPct !== undefined && (
            <span className={`inline-flex items-center gap-1 w-fit text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
              isPP ? 'bg-slate-100 text-slate-600' : (isBudgetFavorable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700')
            }`}>
              {vsBudgetPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {vsBudgetPct >= 0 ? '+' : ''}{vsBudgetPct}{isPP ? 'p.p.' : '%'} vs Orç.
            </span>
          )}
          {yoyChange !== undefined && (
            <span className={`inline-flex items-center gap-1 w-fit text-xs font-semibold px-1.5 py-0.5 rounded whitespace-nowrap ${
              isPP ? 'bg-slate-100 text-slate-600' : (isYoYFavorable ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700')
            }`}>
              {yoyChange >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {yoyChange >= 0 ? '+' : ''}{yoyChange}{isPP ? 'p.p.' : '%'} YoY
            </span>
          )}
        </div>
      )}
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
};

const IndustrialTax = ({ year, language, setActiveSection, flowMap }) => {
  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  
  // Budget data for Industrial Tax (harmonizado com cashFlowData.js)
  const budgetData = {
    profitBeforeTax: 68.4,  // ~95% do EBT real
    incomeTax: -17.1,       // ~95% do imposto real
    effectiveRate: 25,
    retentions: -6.0,       // 2.5% de 240M orçamento
    residualBalance: -11.1,
  };
  
  // Previous year data (2024 de cashFlowData.js)
  const prevYearData = {
    profitBeforeTax: 50.5,  // ebt 2024
    incomeTax: -12.6,       // taxes 2024
    effectiveRate: 24.9,    // 12.6/50.5
    retentions: -6.1,       // 2.5% de 245M (receita 2024)
    residualBalance: -6.5,
  };
  
  const taxInsights = {
    pt: [
      { title: 'VS ORÇAMENTO', description: 'Imposto reconhecido de 18.0M USD, +5.3% face ao Orçamento 2025 (17.1M USD), reflectindo EBT superior ao previsto.', color: 'text-gold-400', icon: 'target' },
      { title: 'RESULTADO VS CAIXA', description: 'O encargo fiscal reconhecido em resultados não coincide necessariamente com a saída de caixa do ano, dado o mecanismo de retenções por conta e liquidação final.', color: 'text-blue-400', icon: 'alert' },
      { title: 'RESULTADO LÍQUIDO', description: 'O Resultado Líquido de 54M USD representa uma margem líquida de 18.1%, confirmando solidez na conversão de receita em lucro.', color: 'text-emerald-400', icon: 'check' },
    ],
    en: [
      { title: 'VS BUDGET', description: 'Recognized tax of 18.0M USD, +5.3% vs Budget 2025 (17.1M USD), reflecting higher than expected EBT.', color: 'text-gold-400', icon: 'target' },
      { title: 'RESULT VS CASH', description: 'The tax charge recognized in results does not necessarily match the year\'s cash outflow, given the withholding and final settlement mechanism.', color: 'text-blue-400', icon: 'alert' },
      { title: 'NET INCOME', description: 'Net Income of 54M USD represents a net margin of 18.1%, confirming solid revenue to profit conversion.', color: 'text-emerald-400', icon: 'check' },
    ],
  };

  const taxData = {
    profitBeforeTax: 72,        // EBT de cashFlowData.js 2025
    incomeTax: -18,             // taxes de cashFlowData.js 2025
    effectiveRate: 25.0,        // 18/72 = 25%
    withholdingPayments: -6.25, // 2.5% de 250M receita (H1: 150M × 2.5% = 3.75M + H2: 100M × 2.5% = 2.5M)
    residualBalance: -11.75,    // 18 - 6.25 = 11.75M
    theoreticalTax: -18,
    fiscalAdjustments: 0,
    statutoryRate: 25,
    h1Revenue: 150,             // 1T: 100M + 2T: 50M
    h2Revenue: 100,             // 3T: 75M + 4T: 25M
    netIncome: 54,              // netIncome de cashFlowData.js 2025
    netMargin: 21.6,            // 54/250 = 21.6%
  };

  // Calculate variations
  const ebtVsBudgetPct = Math.round((taxData.profitBeforeTax - budgetData.profitBeforeTax) / Math.abs(budgetData.profitBeforeTax) * 100);
  const ebtYoYPct = Math.round((taxData.profitBeforeTax - prevYearData.profitBeforeTax) / Math.abs(prevYearData.profitBeforeTax) * 100);
  const taxVsBudgetPct = Math.round((taxData.incomeTax - budgetData.incomeTax) / Math.abs(budgetData.incomeTax) * 100);
  const taxYoYPct = Math.round((taxData.incomeTax - prevYearData.incomeTax) / Math.abs(prevYearData.incomeTax) * 100);
  const rateVsBudgetPP = (taxData.effectiveRate - budgetData.effectiveRate).toFixed(1);
  const rateYoYPP = (taxData.effectiveRate - prevYearData.effectiveRate).toFixed(1);
  const retentionsVsBudgetPct = Math.round((taxData.withholdingPayments - budgetData.retentions) / Math.abs(budgetData.retentions) * 100);
  const retentionsYoYPct = Math.round((taxData.withholdingPayments - prevYearData.retentions) / Math.abs(prevYearData.retentions) * 100);
  const residualVsBudgetPct = Math.round((taxData.residualBalance - budgetData.residualBalance) / Math.abs(budgetData.residualBalance) * 100);
  const residualYoYPct = Math.round((taxData.residualBalance - prevYearData.residualBalance) / Math.abs(prevYearData.residualBalance) * 100);

  return (
    <div className="space-y-6" data-testid="industrial-tax-section">
      {/* Title */}
      <div>
        <button 
          onClick={() => setActiveSection('income-statement')}
          className="flex items-center gap-2 text-sm mb-4 transition-colors hover:opacity-80"
          style={{ color: '#7A859D' }}
        >
          <ArrowLeft className="w-4 h-4" />
          {language === 'pt' ? 'Voltar à Demonstração de Resultados' : 'Back to Income Statement'}
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 
              className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
              style={{ color: '#0E1633', fontFamily: '"Source Serif 4", serif' }}
            >
              {language === 'pt' ? 'Imposto Industrial' : 'Income Tax'} | {titleSuffix}
            </h1>
            <p 
              className="text-[15px] leading-[22px]"
              style={{ color: '#425070' }}
            >
              {comparisonSubtitle}
            </p>
          </div>
          
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {taxInsights[language].map((insight, idx) => {
            const iconMap = { target: Target, alert: AlertTriangle, check: CheckCircle };
            const IconComponent = iconMap[insight.icon] || Target;
            const bgColors = { 'text-gold-400': 'bg-amber-500/30', 'text-blue-400': 'bg-blue-500/30', 'text-emerald-400': 'bg-emerald-500/30' };
            const iconColors = { 'text-gold-400': 'text-amber-400', 'text-blue-400': 'text-blue-400', 'text-emerald-400': 'text-emerald-400' };
            return (
              <div key={idx} className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]">
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bgColors[insight.color]}`}>
                    <IconComponent className={`w-5 h-5 ${iconColors[insight.color]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${insight.color}`}>{insight.title}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{insight.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* KPIs - 5 cards */}
      <div className="grid grid-cols-5 gap-4">
        <MetricCard 
          title={language === 'pt' ? 'Resultado Antes Impostos' : 'Profit Before Tax'} 
          value={taxData.profitBeforeTax} 
          unit="M USD" 
          vsBudgetPct={ebtVsBudgetPct}
          yoyChange={ebtYoYPct}
          tooltipData={{
            title: language === 'pt' ? 'Resultado Antes Impostos' : 'Profit Before Tax',
            definition: language === 'pt' 
              ? 'Base de tributação do exercício: resultado líquido antes da aplicação do imposto industrial.'
              : 'Tax base for the period: net result before applying industrial tax.',
            relevance: language === 'pt'
              ? 'Determina o montante de imposto a reconhecer em resultados.'
              : 'Determines the tax amount to be recognized in results.',
            currentReading: language === 'pt'
              ? `${taxData.profitBeforeTax.toFixed(1)}M USD, ${ebtVsBudgetPct >= 0 ? '+' : ''}${ebtVsBudgetPct}% Orçamento`
              : `${taxData.profitBeforeTax.toFixed(1)}M USD, ${ebtVsBudgetPct >= 0 ? '+' : ''}${ebtVsBudgetPct}% Budget`
          }}
        />
        <MetricCard 
          title={language === 'pt' ? 'Imposto s/ Rendimentos' : 'Income Tax'} 
          value={taxData.incomeTax} 
          unit="M USD" 
          vsBudgetPct={taxVsBudgetPct}
          yoyChange={taxYoYPct}
          tooltipData={{
            title: language === 'pt' ? 'Imposto s/ Rendimentos' : 'Income Tax',
            definition: language === 'pt' 
              ? 'Encargo fiscal reconhecido em resultados do exercício.'
              : 'Tax charge recognized in period results.',
            relevance: language === 'pt'
              ? 'Não coincide necessariamente com a saída de caixa, dado o mecanismo de retenções por conta.'
              : 'Does not necessarily match cash outflow due to withholding payment mechanism.',
            currentReading: language === 'pt'
              ? `${taxData.incomeTax.toFixed(1)}M USD, ${taxVsBudgetPct >= 0 ? '+' : ''}${taxVsBudgetPct}% Orçamento`
              : `${taxData.incomeTax.toFixed(1)}M USD, ${taxVsBudgetPct >= 0 ? '+' : ''}${taxVsBudgetPct}% Budget`
          }}
        />
        <MetricCard 
          title={language === 'pt' ? 'Taxa Efectiva' : 'Effective Rate'} 
          value={taxData.effectiveRate} 
          unit="%" 
          vsBudgetPct={parseFloat(rateVsBudgetPP)}
          yoyChange={parseFloat(rateYoYPP)}
          isPP={true}
          tooltipData={{
            title: language === 'pt' ? 'Taxa Efectiva' : 'Effective Rate',
            definition: language === 'pt' 
              ? 'Taxa efectiva de tributação: imposto reconhecido dividido pelo resultado antes de impostos.'
              : 'Effective tax rate: recognized tax divided by profit before tax.',
            relevance: language === 'pt'
              ? 'Comparar com taxa legal de 25% para avaliar eficiência fiscal.'
              : 'Compare with statutory 25% rate to assess tax efficiency.',
            currentReading: language === 'pt'
              ? `${taxData.effectiveRate}%, ${parseFloat(rateVsBudgetPP) >= 0 ? '+' : ''}${rateVsBudgetPP}p.p. vs Orçamento`
              : `${taxData.effectiveRate}%, ${parseFloat(rateVsBudgetPP) >= 0 ? '+' : ''}${rateVsBudgetPP}p.p. vs Budget`
          }}
        />
        <MetricCard 
          title={language === 'pt' ? 'Retenções por Conta' : 'Withholding Payments'} 
          value={taxData.withholdingPayments} 
          unit="M USD" 
          vsBudgetPct={retentionsVsBudgetPct}
          yoyChange={retentionsYoYPct}
          tooltipData={{
            title: language === 'pt' ? 'Retenções por Conta' : 'Withholding Payments',
            definition: language === 'pt' 
              ? 'Pagamentos antecipados ao Estado: 2,5% da receita semestral (Agosto + Fevereiro).'
              : 'Advance payments to the State: 2.5% of half-yearly revenue (August + February).',
            relevance: language === 'pt'
              ? 'Reduzem o saldo residual a liquidar na declaração final.'
              : 'Reduce the residual balance due in final declaration.',
            currentReading: language === 'pt'
              ? `${taxData.withholdingPayments.toFixed(1)}M USD, ${retentionsVsBudgetPct >= 0 ? '+' : ''}${retentionsVsBudgetPct}% Orçamento`
              : `${taxData.withholdingPayments.toFixed(1)}M USD, ${retentionsVsBudgetPct >= 0 ? '+' : ''}${retentionsVsBudgetPct}% Budget`
          }}
        />
        <MetricCard 
          title={language === 'pt' ? 'Saldo Residual' : 'Residual Balance'} 
          value={taxData.residualBalance} 
          unit="M USD" 
          vsBudgetPct={residualVsBudgetPct}
          yoyChange={residualYoYPct}
          tooltipData={{
            title: language === 'pt' ? 'Saldo Residual' : 'Residual Balance',
            definition: language === 'pt' 
              ? 'Imposto ainda a pagar após retenções por conta. Liquidação em Maio do ano seguinte.'
              : 'Tax still payable after withholding payments. Due in May of following year.',
            relevance: language === 'pt'
              ? 'Valores baixos indicam boa gestão do esforço fiscal de caixa.'
              : 'Low values indicate good cash tax effort management.',
            currentReading: language === 'pt'
              ? `${taxData.residualBalance.toFixed(1)}M USD, ${residualVsBudgetPct >= 0 ? '+' : ''}${residualVsBudgetPct}% Orçamento`
              : `${taxData.residualBalance.toFixed(1)}M USD, ${residualVsBudgetPct >= 0 ? '+' : ''}${residualVsBudgetPct}% Budget`
          }}
        />
      </div>

      {/* Executive Summary */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Imposto industrial' : 'Industrial tax'}
        language={language}
        maxHeight="350px"
        icon={Info}
        dataTestId="industrial-tax-executive-summary"
      >
        <p className="text-sm text-slate-600 leading-relaxed">
          {language === 'pt' 
            ? `O imposto sobre os rendimentos de 2025 cifrou-se em ${Math.abs(taxData.incomeTax).toFixed(1)}M USD, correspondendo a uma taxa efectiva de ${taxData.effectiveRate}%, alinhada com a taxa legal de ${taxData.statutoryRate}%. O esforço fiscal de caixa foi parcialmente absorvido pelas retenções por conta de ${Math.abs(taxData.withholdingPayments).toFixed(1)}M USD (2,5% da receita semestral), resultando num saldo residual de apenas ${Math.abs(taxData.residualBalance).toFixed(1)}M USD a liquidar em Maio de 2026. Esta posição confortável demonstra uma gestão fiscal equilibrada, sem pressão material sobre a tesouraria do exercício seguinte.`
            : `Income tax for 2025 totaled ${Math.abs(taxData.incomeTax).toFixed(1)}M USD, corresponding to an effective rate of ${taxData.effectiveRate}%, aligned with the statutory rate of ${taxData.statutoryRate}%. The cash tax effort was partially absorbed by withholding payments of ${Math.abs(taxData.withholdingPayments).toFixed(1)}M USD (2.5% of half-yearly revenue), resulting in a residual balance of only ${Math.abs(taxData.residualBalance).toFixed(1)}M USD due in May 2026. This comfortable position demonstrates balanced tax management, with no material pressure on next year's treasury.`}
        </p>
      </ExecutiveSummarySection>

      {/* Charts Side by Side */}
      <div className="grid grid-cols-2 gap-4">
        {/* Tax Bridge */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="mb-4">
            <h3 className="font-serif text-base font-bold text-primary">
              {language === 'pt' ? 'Ponte Fiscal' : 'Tax Bridge'}
            </h3>
            <p className="text-slate-400 text-[10px] mt-0.5">
              {language === 'pt' ? '2025 vs Orçamento 2025 vs 2024 (M USD)' : 'Actual 2025 vs Budget 2025 vs Actual 2024 (M USD)'}
            </p>
          </div>
          
          {/* Premium Waterfall Chart with 3 bars */}
          <div className="relative h-52">
            <div className="flex items-end justify-between h-full px-1 relative">
              {/* Bar Group 1: Imposto Reconhecido */}
              <div className="flex-1 flex flex-col items-center">
                <div className="flex gap-1 mb-1">
                  <span className="text-[9px] font-bold text-primary">18.0</span>
                  <span className="text-[9px] font-bold text-gold-600">17.1</span>
                  <span className="text-[9px] text-slate-400">12.6</span>
                </div>
                <div className="flex gap-0.5 items-end" style={{ height: '130px' }}>
                  <div 
                    className="w-5 bg-gradient-to-t from-primary to-primary/80 rounded-t shadow-sm"
                    style={{ height: '130px' }}
                  />
                  <div 
                    className="w-5 bg-gradient-to-t from-gold-500 to-gold-400 rounded-t shadow-sm"
                    style={{ height: '123px' }}
                  />
                  <div 
                    className="w-5 bg-gradient-to-t from-slate-300 to-slate-200 rounded-t"
                    style={{ height: '91px' }}
                  />
                </div>
                <div className="text-[8px] text-slate-500 mt-2 text-center font-medium leading-tight">
                  {language === 'pt' ? 'Imposto\nReconhecido' : 'Recognized\nTax'}
                </div>
              </div>
              
              {/* Arrow */}
              <div className="text-slate-300 text-sm mb-16">→</div>
              
              {/* Bar Group 2: Retenções por Conta */}
              <div className="flex-1 flex flex-col items-center">
                <div className="flex gap-1 mb-1">
                  <span className="text-[9px] font-bold text-emerald-600">-6.3</span>
                  <span className="text-[9px] font-bold text-gold-600">-6.0</span>
                  <span className="text-[9px] text-slate-400">-6.1</span>
                </div>
                <div className="flex gap-0.5 items-end" style={{ height: '130px' }}>
                  <div 
                    className="w-5 bg-gradient-to-t from-emerald-500 to-emerald-400 rounded-t shadow-sm"
                    style={{ height: '45px' }}
                  />
                  <div 
                    className="w-5 bg-gradient-to-t from-gold-500 to-gold-400 rounded-t shadow-sm"
                    style={{ height: '43px' }}
                  />
                  <div 
                    className="w-5 bg-gradient-to-t from-slate-300 to-slate-200 rounded-t"
                    style={{ height: '44px' }}
                  />
                </div>
                <div className="text-[8px] text-slate-500 mt-2 text-center font-medium leading-tight">
                  {language === 'pt' ? 'Retenções\npor Conta' : 'Withholding\nPayments'}
                </div>
              </div>
              
              {/* Arrow */}
              <div className="text-slate-300 text-sm mb-16">→</div>
              
              {/* Bar Group 3: Saldo Residual */}
              <div className="flex-1 flex flex-col items-center">
                <div className="flex gap-1 mb-1">
                  <span className="text-[9px] font-bold text-amber-600">11.7</span>
                  <span className="text-[9px] font-bold text-gold-600">11.1</span>
                  <span className="text-[9px] text-slate-400">6.5</span>
                </div>
                <div className="flex gap-0.5 items-end" style={{ height: '130px' }}>
                  <div 
                    className="w-5 bg-gradient-to-t from-amber-500 to-amber-400 rounded-t shadow-sm"
                    style={{ height: '85px' }}
                  />
                  <div 
                    className="w-5 bg-gradient-to-t from-gold-500 to-gold-400 rounded-t shadow-sm"
                    style={{ height: '81px' }}
                  />
                  <div 
                    className="w-5 bg-gradient-to-t from-slate-300 to-slate-200 rounded-t"
                    style={{ height: '47px' }}
                  />
                </div>
                <div className="text-[8px] text-slate-500 mt-2 text-center font-medium leading-tight">
                  {language === 'pt' ? 'Saldo\nResidual' : 'Residual\nBalance'}
                </div>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex justify-center gap-3 mt-3 pt-3 border-t border-slate-100 text-[9px]">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-primary rounded-sm"></div>
              <span className="text-slate-500">2025</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-gold-500 rounded-sm"></div>
              <span className="text-slate-500">Orç. 2025</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-slate-300 rounded-sm"></div>
              <span className="text-slate-500">2024</span>
            </div>
          </div>
        </div>

        {/* Withholding Detail */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="mb-3">
            <h3 className="font-serif text-base font-bold text-primary">
              {language === 'pt' ? 'Detalhe das Retenções' : 'Withholding Detail'}
            </h3>
            <p className="text-slate-400 text-[10px] mt-0.5">
              {language === 'pt' ? '2,5% da receita semestral' : '2.5% of half-yearly revenue'}
            </p>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs font-medium text-slate-700">{language === 'pt' ? '1º Semestre 2025' : 'H1 2025'}</p>
                <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Pago em Agosto 2025' : 'Paid August 2025'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">{language === 'pt' ? 'Receita' : 'Revenue'}: 150M</p>
                <p className="text-sm font-bold text-primary">3.75M <span className="text-[10px] font-normal text-slate-400">(2.5%)</span></p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-xs font-medium text-slate-700">{language === 'pt' ? '2º Semestre 2025' : 'H2 2025'}</p>
                <p className="text-[10px] text-slate-500">{language === 'pt' ? 'Pago em Fevereiro 2026' : 'Paid February 2026'}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">{language === 'pt' ? 'Receita' : 'Revenue'}: 100M</p>
                <p className="text-sm font-bold text-primary">2.5M <span className="text-[10px] font-normal text-slate-400">(2.5%)</span></p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
              <div>
                <p className="text-xs font-bold text-amber-800">{language === 'pt' ? 'Saldo Residual' : 'Residual Balance'}</p>
                <p className="text-[10px] text-amber-600">{language === 'pt' ? 'Liquidação em Maio 2026' : 'Settlement May 2026'}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold text-amber-700">11.75M</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustrialTax;
